import { ContactForm } from '../components/ContactForm';
import { Icon, type IconName } from '../components/Icon';
import { Seo } from '../components/Seo';
import { siteConfig } from '../config/site';
import { useMoonbase } from '../lib/moonbase';

type SupportAction = {
  icon: IconName;
  title: string;
  description: string;
  label: string;
  action: () => void;
};

const faqs = [
  {
    question: 'How do I download a plugin again?',
    answer:
      'Open Downloads & licenses and sign in with the email connected to your order. Moonbase keeps your owned products, current installers and activation controls under that account.',
  },
  {
    question: 'I reached my activation limit. What should I do?',
    answer:
      'Sign in to your account and remove an activation from a computer you no longer use. If the unavailable machine used offline activation, contact support so the activation can be reviewed manually.',
  },
  {
    question: 'Can I authorize a studio computer with no internet connection?',
    answer:
      'Yes. The plugin can export a device-token file. Upload that file through an internet-connected device, download the signed Moonbase license file and import it on the offline computer.',
  },
  {
    question: 'I no longer have access to the email used for purchase.',
    answer:
      'Contact support with the order number, original email, approximate purchase date and any payment details that safely help establish ownership. Account changes require manual verification.',
  },
  {
    question: 'Where should the plugins appear after installation?',
    answer:
      'Audio Unit plugins appear in compatible macOS hosts after validation. VST3 plugins are installed into the standard system VST3 location. Restart the host or run a plugin rescan after installation.',
  },
  {
    question: 'Are website licenses and iOS AUv3 purchases the same?',
    answer:
      'Desktop website purchases use Moonbase. An AUv3 distributed through Apple’s App Store normally uses the App Store and StoreKit entitlement flow. Cross-platform ownership rules should be stated on each product page.',
  },
];

export function SupportPage() {
  const moonbase = useMoonbase();
  const fallback = moonbase.openSetup;
  const actions: SupportAction[] = [
    {
      icon: 'download',
      title: 'Downloads',
      description: 'Get current installers for every product in your library.',
      label: 'View my products',
      action: moonbase.configured ? moonbase.viewProducts : fallback,
    },
    {
      icon: 'account',
      title: 'Account recovery',
      description: 'Sign in, reset your password and review your purchase account.',
      label: 'Open account',
      action: moonbase.configured ? moonbase.viewAccount : fallback,
    },
    {
      icon: 'key',
      title: 'Offline activation',
      description: 'Upload a device token and retrieve a signed license file.',
      label: 'Activate offline',
      action: moonbase.configured ? moonbase.activateProduct : fallback,
    },
    {
      icon: 'spark',
      title: 'Voucher or code',
      description: 'Redeem a promotional, educational or partner voucher.',
      label: 'Redeem code',
      action: moonbase.configured ? moonbase.redeemVoucher : fallback,
    },
  ];

  return (
    <>
      <Seo title="Support" description="Downloads, account recovery, activation help and technical support for Wavecraft Audio plugins." path="/support" />
      <section className="page-hero support-hero section--grain">
        <div className="shell page-hero__grid">
          <div>
            <span className="eyebrow eyebrow--line">Support center</span>
            <h1>Start with the fastest route back to your session.</h1>
          </div>
          <div className="page-hero__aside">
            <p>Most download and activation issues can be handled directly through your account. The support form below is ready for anything that needs a person.</p>
            <a className="inline-email" href={`mailto:${siteConfig.company.supportEmail}`}><Icon name="mail" />{siteConfig.company.supportEmail}</a>
          </div>
        </div>
      </section>

      <section className="section support-actions-section">
        <div className="shell support-action-grid">
          {actions.map((item, index) => (
            <button className="support-action" type="button" onClick={item.action} key={item.title}>
              <span className="support-action__number">0{index + 1}</span>
              <Icon name={item.icon} />
              <h2>{item.title}</h2>
              <p>{item.description}</p>
              <span className="support-action__link">{item.label}<Icon name="arrow" /></span>
            </button>
          ))}
        </div>
      </section>

      <section className="section faq-section">
        <div className="shell faq-section__grid">
          <div className="faq-section__intro">
            <span className="eyebrow">Common questions</span>
            <h2>Licenses, installations and account access.</h2>
            <p>These answers are written around the included Moonbase flow and should be adjusted to your exact activation limits and refund policy.</p>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <details key={faq.question} open={index === 0}>
                <summary><span>{faq.question}</span><Icon name="chevron" /></summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section contact-section">
        <div className="shell contact-section__grid">
          <div>
            <span className="eyebrow">Contact support</span>
            <h2>Tell us exactly what happened.</h2>
            <p>Include the plugin version, macOS version, host application and plugin format. A reproducible sequence is more useful than a general description.</p>
            <div className="response-note"><Icon name="clock" /><span><strong>Set your response window</strong>Replace this with your normal support hours and expected reply time.</span></div>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
}
